import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  atualizarBanco,
  atualizarConta,
  atualizarEmpresa,
  atualizarUsuario,
  buscarBanco,
  buscarBancos,
  buscarConta,
  buscarContas,
  buscarEmpresa,
  buscarEmpresas,
  buscarPerfis,
  buscarUsuario,
  buscarUsuarios,
  criarBanco,
  criarConta,
  criarEmpresa,
  criarUsuario,
} from "./api";
import type {
  BancoIn,
  BancoOut,
  BancoPatch,
  ContaCorrenteIn,
  ContaCorrenteOut,
  ContaCorrentePatch,
  EmpresaIn,
  EmpresaOut,
  EmpresaPatch,
  PerfilOut,
  UsuarioIn,
  UsuarioOut,
  UsuarioPatch,
} from "./tipos";

/*
 * Hooks de server-state do modulo de Cadastros (FE-4 CP-A — ADR 0026 §Estado /
 * TanStack Query). Toda leitura/escrita de servidor passa pelo Query: cache,
 * dedup e revalidacao com a config conservadora do `criarQueryClient`
 * (staleTime 30s, retry 1 em query; `mutations:{retry:0}`).
 *
 * Convencoes de queryKey: prefixo `"cadastros"` + recurso + parametros que afetam
 * o resultado (id, empresaId). Chaves estaveis garantem cache hit e refetch
 * correto. As contas sao nested por empresa: a key carrega o `empresaId`.
 *
 * Mutations NAO setam `retry`: `criarQueryClient` ja aplica `mutations:{retry:0}`.
 * No `onSuccess` cada mutacao invalida a(s) query(ies) que ela tornou stale (a
 * lista do recurso e, quando aplicavel, o item individual), para a UI refletir o
 * estado novo no proximo fetch.
 */

/* ───────────────────────── Empresas ───────────────────────── */

/** Lista de empresas no escopo do usuario. */
export function useEmpresas(): UseQueryResult<EmpresaOut[]> {
  return useQuery({
    queryKey: ["cadastros", "empresas"],
    queryFn: buscarEmpresas,
  });
}

/**
 * Uma empresa por id. So dispara quando ha id (`id !== null`) — antes disso a
 * query fica ociosa (`enabled: false`), sem fetch nem erro.
 */
export function useEmpresa(id: string | null): UseQueryResult<EmpresaOut> {
  return useQuery({
    queryKey: ["cadastros", "empresa", id],
    // `id as string` e seguro: `enabled` garante que a queryFn so roda com id
    // nao-nulo. O TanStack so invoca a queryFn quando `enabled` e true.
    queryFn: () => buscarEmpresa(id as string),
    enabled: id !== null,
  });
}

/** Cria empresa; no sucesso invalida a lista de empresas. */
export function useCriarEmpresa(): UseMutationResult<
  EmpresaOut,
  unknown,
  EmpresaIn
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (corpo: EmpresaIn) => criarEmpresa(corpo),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["cadastros", "empresas"] }),
  });
}

/** Argumento de atualizacao de empresa: id + campos parciais. */
interface AtualizarEmpresaArgs {
  id: string;
  corpo: EmpresaPatch;
}

/**
 * Atualiza empresa; no sucesso invalida a lista E o item individual (ambos
 * ficaram stale).
 */
export function useAtualizarEmpresa(): UseMutationResult<
  EmpresaOut,
  unknown,
  AtualizarEmpresaArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: AtualizarEmpresaArgs) =>
      atualizarEmpresa(id, corpo),
    onSuccess: (_dados, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: ["cadastros", "empresas"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cadastros", "empresa", id],
      });
    },
  });
}

/* ───────────────────────── Bancos ───────────────────────── */

/** Catalogo global de bancos. */
export function useBancos(): UseQueryResult<BancoOut[]> {
  return useQuery({
    queryKey: ["cadastros", "bancos"],
    queryFn: buscarBancos,
  });
}

/** Um banco por id. Ocioso enquanto `id === null`. */
export function useBanco(id: string | null): UseQueryResult<BancoOut> {
  return useQuery({
    queryKey: ["cadastros", "banco", id],
    queryFn: () => buscarBanco(id as string),
    enabled: id !== null,
  });
}

/** Cria banco; no sucesso invalida o catalogo. */
export function useCriarBanco(): UseMutationResult<BancoOut, unknown, BancoIn> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (corpo: BancoIn) => criarBanco(corpo),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["cadastros", "bancos"] }),
  });
}

/** Argumento de atualizacao de banco: id + campos parciais. */
interface AtualizarBancoArgs {
  id: string;
  corpo: BancoPatch;
}

/** Atualiza banco; no sucesso invalida o catalogo E o item individual. */
export function useAtualizarBanco(): UseMutationResult<
  BancoOut,
  unknown,
  AtualizarBancoArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: AtualizarBancoArgs) => atualizarBanco(id, corpo),
    onSuccess: (_dados, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: ["cadastros", "bancos"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cadastros", "banco", id],
      });
    },
  });
}

/* ───────────────────────── Contas correntes ───────────────────────── */

/**
 * Contas de uma empresa. So dispara quando ha empresa selecionada
 * (`empresaId !== null`) — antes disso a query fica ociosa (`enabled: false`),
 * sem fetch nem erro. A queryKey inclui `empresaId`: trocar a empresa e uma query
 * distinta no cache.
 */
export function useContas(
  empresaId: string | null,
): UseQueryResult<ContaCorrenteOut[]> {
  return useQuery({
    queryKey: ["cadastros", "contas", empresaId],
    queryFn: () => buscarContas(empresaId as string),
    enabled: empresaId !== null,
  });
}

/**
 * Uma conta por empresa+id. Ocioso enquanto faltar empresa ou conta — a conta
 * nested exige AMBOS os ids no path.
 */
export function useConta(
  empresaId: string | null,
  contaId: string | null,
): UseQueryResult<ContaCorrenteOut> {
  return useQuery({
    queryKey: ["cadastros", "conta", empresaId, contaId],
    queryFn: () => buscarConta(empresaId as string, contaId as string),
    enabled: empresaId !== null && contaId !== null,
  });
}

/**
 * Cria conta na `empresaId` fixada no hook (a tela ja sabe a empresa em foco).
 * No sucesso invalida a lista de contas DAQUELA empresa.
 */
export function useCriarConta(
  empresaId: string,
): UseMutationResult<ContaCorrenteOut, unknown, ContaCorrenteIn> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (corpo: ContaCorrenteIn) => criarConta(empresaId, corpo),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["cadastros", "contas", empresaId],
      }),
  });
}

/** Argumento de atualizacao de conta: contaId + campos parciais. */
interface AtualizarContaArgs {
  contaId: string;
  corpo: ContaCorrentePatch;
}

/**
 * Atualiza conta na `empresaId` fixada no hook. No sucesso invalida a lista de
 * contas DAQUELA empresa E o item individual.
 */
export function useAtualizarConta(
  empresaId: string,
): UseMutationResult<ContaCorrenteOut, unknown, AtualizarContaArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contaId, corpo }: AtualizarContaArgs) =>
      atualizarConta(empresaId, contaId, corpo),
    onSuccess: (_dados, { contaId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["cadastros", "contas", empresaId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cadastros", "conta", empresaId, contaId],
      });
    },
  });
}

/* ───────────────────────── Usuarios ───────────────────────── */

/** Lista de usuarios. */
export function useUsuarios(): UseQueryResult<UsuarioOut[]> {
  return useQuery({
    queryKey: ["cadastros", "usuarios"],
    queryFn: buscarUsuarios,
  });
}

/** Um usuario por id. Ocioso enquanto `id === null`. */
export function useUsuario(id: string | null): UseQueryResult<UsuarioOut> {
  return useQuery({
    queryKey: ["cadastros", "usuario", id],
    queryFn: () => buscarUsuario(id as string),
    enabled: id !== null,
  });
}

/** Cria usuario; no sucesso invalida a lista de usuarios. */
export function useCriarUsuario(): UseMutationResult<
  UsuarioOut,
  unknown,
  UsuarioIn
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (corpo: UsuarioIn) => criarUsuario(corpo),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["cadastros", "usuarios"] }),
  });
}

/** Argumento de atualizacao de usuario: id + campos parciais. */
interface AtualizarUsuarioArgs {
  id: string;
  corpo: UsuarioPatch;
}

/** Atualiza usuario; no sucesso invalida a lista E o item individual. */
export function useAtualizarUsuario(): UseMutationResult<
  UsuarioOut,
  unknown,
  AtualizarUsuarioArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: AtualizarUsuarioArgs) =>
      atualizarUsuario(id, corpo),
    onSuccess: (_dados, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: ["cadastros", "usuarios"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cadastros", "usuario", id],
      });
    },
  });
}

/* ───────────────────────── Perfis ───────────────────────── */

/** Catalogo read-only de perfis. */
export function usePerfis(): UseQueryResult<PerfilOut[]> {
  return useQuery({
    queryKey: ["cadastros", "perfis"],
    queryFn: buscarPerfis,
  });
}
